import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Table, TableProps } from 'antd'
import { CCard, CCardBody, CCardHeader } from '@coreui/react-pro'
import { ColumnsType } from 'antd/lib/table'
import {
  useColumnLocalSort,
  useColumnSearch,
  useTableSearchParams,
} from 'src/libs'
import { ErrorScreen } from 'src/shared/components/errorScreen/errorScreen'
import { DemoContestClient as DemoContestClientType } from './types'
import { setDemoContestRequestError } from './model'
import { useContestClientList } from './hooks'
import moment from 'moment'

export const DemoContestClient: React.FC = () => {
  const [searchParams] = useSearchParams()
  const {
    contestClients,
    onChangeContestClientList,
    loading,
    errorDemoContestRequest,
  } = useContestClientList()

  const getSearchText = (): Record<string, string> => {
    const contestName = searchParams.get('searchField_contestName')
    return contestName ? { contestName } : {}
  }

  const sorterFields = ['activatesAt', 'endsAt']
  const searchFields = ['contestName']
  const handleTableChange: TableProps<DemoContestClientType>['onChange'] =
    useTableSearchParams({
      searchFields,
      sorterFields,
    })

  useEffect(() => {
    onChangeContestClientList({
      sorterField: searchParams.get('sorterField') ?? '-activatesAt',
      searchText: getSearchText(),
    })
  }, [searchParams])

  const contestNameSearch = useColumnSearch<DemoContestClientType>({
    dataIndex: 'contestName',
    placeholder: 'Contest Name',
  })

  const columns: ColumnsType<DemoContestClientType> = [
    {
      title: 'Name',
      dataIndex: 'contestName',
      key: 'contestName',
      ...contestNameSearch,
    },
    {
      title: 'Contest status',
      dataIndex: 'contestStatus',
    },
    {
      title: 'Activates at',
      dataIndex: 'activatesAt',
      sorter: (a, b) =>
        // @ts-ignore
        new Date(
          moment(a.activatesAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL'),
        ) -
        // @ts-ignore
        new Date(
          moment(b.activatesAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL'),
        ),
      ...useColumnLocalSort<DemoContestClientType>({
        dataIndex: 'activatesAt',
        initialValue: 'descend',
      }),
    },
    {
      title: 'Ends at',
      dataIndex: 'endsAt',
      sorter: (a, b) =>
        // @ts-ignore
        new Date(moment(a.endsAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL')) -
        // @ts-ignore
        new Date(moment(b.endsAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL')),
      ...useColumnLocalSort<DemoContestClientType>({
        dataIndex: 'endsAt',
      }),
    },
    {
      title: 'Client place',
      dataIndex: 'place',
    },
    {
      title: 'Client status',
      dataIndex: 'participantStatus',
    },
    {
      title: 'Award',
      dataIndex: 'prizeAmount',
    },
  ]

  if (![0, 404].includes(errorDemoContestRequest.code)) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          onChangeContestClientList({
            sorterField: searchParams.get('sorterField') ?? '-activatesAt',
            searchText: getSearchText(),
          })

          setDemoContestRequestError({
            message: '',
            code: 0,
          })
        }}
      />
    )
  }

  return (
    <CCard>
      <CCardHeader>
        <strong>Demo contest</strong>
      </CCardHeader>
      <CCardBody>
        <Table
          dataSource={contestClients}
          bordered={true}
          scroll={{ x: true }}
          loading={loading}
          rowKey="contestClientsId"
          columns={columns}
          onChange={handleTableChange}
        />
      </CCardBody>
    </CCard>
  )
}
